import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BookOpenText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Token tidak ditemukan di URL");
      return;
    }
    if (next.length < 6) {
      toast.error("Password baru minimal 6 karakter");
      return;
    }
    if (next !== confirm) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-10 w-10 rounded-md bg-zinc-900 flex items-center justify-center">
            <BookOpenText className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-lg font-display font-semibold text-zinc-900 tracking-tight">Buku Stok</div>
            <div className="text-xs text-zinc-500">Set password baru</div>
          </div>
        </div>
        <div className="border border-zinc-200 rounded-md p-6 bg-white shadow-sm" data-testid="reset-password-card">
          {done ? (
            <div className="text-center space-y-3" data-testid="reset-password-done">
              <div className="h-12 w-12 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mx-auto text-2xl">
                ✓
              </div>
              <h1 className="text-lg font-semibold text-zinc-900">Password berhasil diubah</h1>
              <p className="text-sm text-zinc-500">
                Anda akan diarahkan ke halaman login…
              </p>
            </div>
          ) : !token ? (
            <div className="text-center space-y-3">
              <h1 className="text-lg font-semibold text-rose-700">Link tidak valid</h1>
              <p className="text-sm text-zinc-500">
                Buka link reset dari email Anda, atau minta ulang dari halaman Lupa Password.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block text-sm text-zinc-900 underline"
              >
                Minta Link Baru
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-zinc-900 mb-1">Set Password Baru</h1>
              <p className="text-sm text-zinc-500 mb-5">
                Silakan buat password baru untuk akun Anda.
              </p>
              <form onSubmit={onSubmit} className="space-y-3" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="rp-next">Password Baru (min 6)</Label>
                  <Input
                    id="rp-next"
                    data-testid="rp-next"
                    type="password"
                    autoComplete="new-password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <Label htmlFor="rp-confirm">Ulangi Password Baru</Label>
                  <Input
                    id="rp-confirm"
                    data-testid="rp-confirm"
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="rp-submit"
                  className="w-full bg-zinc-900 hover:bg-zinc-800 h-10 mt-2"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Password Baru"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
